import React, { useCallback, useEffect, useRef } from "react";
import styles from "./styles.module.scss";
import classNames from "classnames";
import Alert from "components/base/alert/alert";
import { getFormattedProgress } from "helpers/string.helper";
import { EventTypes } from "models/dto/ZoomiLxp/Utilities/Enumerations/EventTypes";
import { useAppStore } from "store";
import { usePlayerNav } from "hooks/usePlayerNav";
import PlayerControl, { ControlParams } from "../player-control/player-control";
import { IPlayerExternalLinkBoxProps } from "pages/private/cms-external-link-page/constants";
import { usePlayerControl } from "hooks/usePlayerControl";
import { ISaveEventCourseItemHistoryModel } from "models/dto/ZoomiLxp/Models/EventModels/ISaveEventCourseItemHistoryModel";

const cx = classNames.bind(styles);

const PlayerExternalLinkBox = (props: IPlayerExternalLinkBoxProps) => {
	const { externalContent, className, onEventHandle } = props;
	const {
		contentStore,
		coursesStore,
		toasterStore,
		commonStore: { alertEventBus },
	} = useAppStore();
	const wasEndEventSentRef = useRef(false);
	const playingRef = useRef(false);
	const currentPositionRef = useRef(0);
	const sessionIdRef = useRef<number>(contentStore.sessionId ?? 0);

	const { prev, next } = usePlayerNav();

	const handleNext = () => next();

	const handlePrev = () => prev();

	const handleClick = () => {
		const data: ISaveEventCourseItemHistoryModel = {
			sessionId: contentStore?.sessionId ?? 0,
			eventType: EventTypes.Complete,
			payload: "",
		};
		onEventHandle(data).then(() => {
			coursesStore.curActiveContent.completed = true;
		});
	};

	const initParams: ControlParams = {
		currentProgress: 1,
		totalProgress: 1,
		boxRef: useRef<HTMLDivElement>(null),
		getFormattedProgress,
		muted: true,
		playing: false,
		isDownload: false,
		handleNext,
		handlePrev,
		isPrev: false,
		isNext: false,
	};

	const sendEndEventOnce = async () => {
		if (wasEndEventSentRef.current) return;
		wasEndEventSentRef.current = true;

		const currentPosition = currentPositionRef.current;
		const isPlaying = playingRef.current;
		const sessionId = sessionIdRef.current;

		const data: ISaveEventCourseItemHistoryModel = {
			sessionId,
			eventType: EventTypes.End,
			payload: JSON.stringify({
				currentPosition,
				isPlaying,
			}),
		};

		contentStore.stopKeepAlive();

		await onEventHandle(data);
	};

	const [params, setParams] = React.useState<ControlParams>(initParams);

	useEffect(() => {
		playingRef.current = params.playing;
	}, [params.playing]);

	useEffect(() => {
		currentPositionRef.current = params.currentProgress ?? 0;
	}, [params.currentProgress]);

	useEffect(() => {
		if (contentStore.sessionId) {
			sessionIdRef.current = contentStore.sessionId;
		}
	}, [contentStore.sessionId]);

	usePlayerControl(setParams);

	const sendStart = useCallback(() => {
		const data: ISaveEventCourseItemHistoryModel = {
			sessionId: contentStore?.sessionId ?? 0,
			eventType: EventTypes.Start,
			payload: "",
		};
		contentStore
			.sendEventCourseItemHistory(data)
			.catch((err) => toasterStore.showErrorMessage(err));
	}, [contentStore, toasterStore]);

	useEffect(() => {
		if (contentStore.sessionId) {
			sendStart();
		}
	}, [contentStore.sessionId, sendStart]);

	useEffect(
		() => {
			return () => {
				void sendEndEventOnce();
			};
		}, //eslint-disable-next-line
		[]
	);

	if (!externalContent) return <></>;

	return (
		<div className={cx(styles.player_external_link_box, className)} ref={params.boxRef}>
			<Alert eventBus={alertEventBus} />
			<div className={styles.player_external_link_box__wrap}>
				<div className={styles.player_external_link_box__color_bar} />
				<div className={styles.player_external_link_box__body}>
					<div className={styles.player_external_link_box__title}>{externalContent.title}</div>
					<div className={styles.player_external_link_box__description}>{externalContent.description}</div>
				</div>
				<div className={styles.player_external_link_box__footer}>
					<a
						href={externalContent.url}
						target="_blank"
						className={styles.player_external_link_box__btn}
						rel="noreferrer"
						onClick={handleClick}
					>
						Show Resource
					</a>
				</div>
			</div>
			<PlayerControl params={params} />
		</div>
	);
};

export default PlayerExternalLinkBox;
